/**
 * The idempotency contract, client side.
 *
 * Invariant 3, and D-38: **the key belongs to the logical operation, not to the attempt.**
 * A fresh key per attempt turns a retry into a second goods receipt, and a duplicated
 * receipt is not noticed until a physical count months later. The server refuses a write
 * with no key (400) rather than generating one, precisely so this decision cannot be made
 * accidentally by the transport.
 *
 * The shape below makes the correct thing the easy thing:
 *
 *   * `beginOperation()` mints one key and hands back a handle.
 *   * every attempt at that operation passes the SAME handle.
 *   * the handle is retired only once the operation has succeeded, or once the person
 *     abandons it and starts a new one.
 *
 * Cross-process retries work too. §8 of PROJECT.md says an offline-origin transaction
 * carries a client UUID used as the idempotency key; `operationFromKey` takes that UUID
 * so a queued mobile write keeps its identity when it finally drains, even from a
 * different device session. In remote mode the record lives in the canonical service
 * (D-37), so a retry that lands on a different host application is still deduplicated.
 */
export class IdempotencyKeyError extends Error {
    name = 'IdempotencyKeyError';
}
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * A v4 UUID.
 *
 * `crypto.randomUUID` is present in every browser the five host applications support and
 * in Node 20. The fallback covers a non-secure context (plain http on a warehouse LAN),
 * where `crypto.randomUUID` is unavailable in some browsers but `getRandomValues` is not.
 */
export function newIdempotencyKey() {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function')
        return c.randomUUID();
    if (c && typeof c.getRandomValues === 'function') {
        const bytes = c.getRandomValues(new Uint8Array(16));
        // Bytes 6 and 8 carry the version and variant. Indexed writes need the guard under
        // noUncheckedIndexedAccess; a 16-byte array always has them, but the compiler is right
        // to insist rather than take our word for it.
        bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
        bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
        const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    throw new IdempotencyKeyError('no cryptographic random source is available, so an idempotency key cannot be minted. ' +
        'Supply one explicitly with operationFromKey() rather than posting without a key.');
}
/** Start a new logical operation. Call this once per user intent, not once per attempt. */
export function beginOperation() {
    return { key: newIdempotencyKey(), attempts: 0 };
}
/**
 * Adopt a key minted elsewhere — an offline queue entry, an integration's correlation id.
 *
 * Rejects anything that is not a UUID. A key that is not globally unique is worse than no
 * key at all: two different operations colliding on one key means the second is answered
 * with the first one's stored response, and neither caller finds out.
 */
export function operationFromKey(key) {
    if (!UUID_PATTERN.test(key)) {
        throw new IdempotencyKeyError(`idempotency key must be a UUID, got ${JSON.stringify(key)}`);
    }
    return { key, attempts: 0 };
}
/** Record an attempt. Returns a new handle carrying the SAME key. */
export function nextAttempt(operation) {
    return { key: operation.key, attempts: operation.attempts + 1 };
}
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
/** Set by the server when a stored response was served instead of doing the work again. */
export const REPLAY_HEADER = 'Idempotent-Replay';
//# sourceMappingURL=idempotency.js.map